import * as React from 'react';
import {useEffect} from 'react';
import type {SectionTransforms, TreeState} from '@sail/react-aria';
import {
  getInteractionModality,
  setInteractionModality,
  useCollectionState,
  useId,
  useItem,
  useListBoxSection,
} from '@sail/react-aria';
import type {View} from '@sail/engine';
import {assignProps, view, createViewConfig} from '@sail/engine';
import {chevronDown} from '@sail/icons/react/Icon';
import {Checkbox} from './Checkbox';
import {Counter} from './Counter';
import {Header} from './Header';
import {Icon} from './Icon';
import useFauxTreeState from '../hooks/useFauxTreeState';
import {Label} from './Label';
import {CollectionGroupContext} from './CollectionGroupContext';

export type FauxTreeGroupProps = {
  id?: string;
  /**
   * A heading grouping the menu items.
   */
  title?: React.ReactNode;
  /** Child items that belong to the group. */
  children?: React.ReactNode;

  textValue?: string;

  counterEnabled?: boolean;

  /**
   * If true, an option to select/unselect all items in a group is visible in each group.
   */
  groupSelectionAllEnabled?: boolean;

  /**
   * An accessibility label for the group.
   */
  'aria-label'?: string;

  subviews?: View.Subviews<{
    header: typeof Header;
    list: 'div';
    // collapsibleIcon: typeof Icon;
  }>;
};

export const FauxTreeGroupTransforms: SectionTransforms<
  FauxTreeGroupProps,
  'id'
> = {
  idField: 'id',
  makeGenericItem(props: FauxTreeGroupProps) {
    return {
      type: 'section',
      'aria-label': props['aria-label'],
      textValue:
        typeof props.title === 'string'
          ? props.title
          : props.textValue ?? props['aria-label'] ?? '',
    };
  },
};

export const FauxTreeGroupConfig = createViewConfig({
  name: 'FauxTreeGroup',
  props: {} as FauxTreeGroupProps,
  defaults: {
    counterEnabled: true,
  },
});

export const FauxTreeGroup = FauxTreeGroupConfig.createView(
  function FauxTreeGroup({
    id,
    children,
    title,
    textValue,
    subviews,
    counterEnabled,
    groupSelectionAllEnabled,
    ...props
  }) {
    const headerRef = React.useRef<HTMLElement>(null);
    const key = useItem(
      id,
      FauxTreeGroupTransforms.makeGenericItem({
        'aria-label': props['aria-label'],
        title,
        textValue,
      }),
    );

    const accessibleLabel =
      props['aria-label'] ??
      (typeof title === 'string' ? undefined : textValue);

    const {
      itemProps,
      headingProps: {id: headingId},
      groupProps,
    } = useListBoxSection({
      heading: title,
      'aria-label': accessibleLabel,
    });

    const state = useCollectionState();

    const tree = state as TreeState;
    const isOpen = !tree.expandable || tree.expandedKeys.has(key);

    const {selectGroupItems, isMultipleSelection} = useFauxTreeState();

    const selectGroupItem = selectGroupItems.get(key) ?? {
      selectedCount: 0,
      totalCount: 0,
      selectedForeignCount: 0,
      checkableKeys: [],
      uncheckableKeys: [],
      isDisabled: false,
    };

    const {
      selectedCount,
      totalCount,
      selectedForeignCount,
      checkableKeys,
      uncheckableKeys,
      isDisabled,
    } = selectGroupItem;

    const areAllSelected = totalCount === selectedCount;
    const areAllEnabledSelected =
      checkableKeys.length - selectedForeignCount === selectedCount;
    const hasItemsSelected = selectedCount > 0;

    const groupId = useId();
    const triggerProps = {
      role: 'button',
      'aria-label': accessibleLabel,
      'aria-expanded': isOpen,
      'aria-controls': groupId,
      ...(isMultipleSelection ? {tabIndex: 0} : {}),
      onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => {
        if (
          event.target === event.currentTarget &&
          (event.key === 'Enter' || event.key === ' ')
        ) {
          event.preventDefault();
          tree.selectionManager.setFocusedKey(null);
          tree.toggleKey(key);
        }
      },
    };

    const focusedItem = state.collection.getItem(
      state.selectionManager.focusedKey,
    );

    // The mount-time autofocus-onto-selection (see useSelectableCollection's
    // mount effect) can retain the modality that opened the list even though
    // it isn't actual keyboard navigation. Track the first focused item so we
    // can distinguish that case from a real initial keyboard move.
    const isInitialMount = React.useRef(true);

    useEffect(() => {
      // Single-selection groups always reveal the focused item by expanding
      // its ancestor groups, regardless of how focus got there — this
      // includes the mount-time autofocus-onto-selection described above.
      //
      // Multi-selection groups restrict this to keyboard- or virtual-driven
      // focus changes (arrow keys, typeahead, or assistive technology) that
      // happen after mount. A mouse-click open also autofocuses the first
      // selected item, which would otherwise force every collapsed group open
      // on every click — multi-select lists default to collapsed groups
      // instead.
      // Keep the mount gate armed until focus is actually assigned. If that
      // initial focus lands on a selection in a collapsed group, return focus
      // to the visible listbox so genuine navigation can target and reveal an
      // option.
      if (!focusedItem) {
        return;
      }

      // Find all collapsed ancestors of the focused item.
      let {parentKey} = focusedItem;
      const keysToExpand = new Set<string>();

      while (parentKey) {
        keysToExpand.add(parentKey as string);
        const parent = tree.collection.getItem(parentKey);
        parentKey =
          parentKey === parent?.parentKey ? undefined : parent?.parentKey;
      }

      const hasCollapsedAncestor = Array.from(keysToExpand).some(
        (key) => !(tree.expandedKeys as Set<string>).has(key),
      );
      const isMountTimeAutofocusOnCollapsedSelection =
        isMultipleSelection &&
        isInitialMount.current &&
        hasCollapsedAncestor &&
        state.selectionManager.isSelected(focusedItem.key);
      isInitialMount.current = false;

      if (isMountTimeAutofocusOnCollapsedSelection) {
        state.selectionManager.setFocusedKey(null);
        return;
      }

      const interactionModality = getInteractionModality();
      const isFocusNavigation =
        interactionModality === 'keyboard' || interactionModality === 'virtual';
      if (hasCollapsedAncestor && (!isMultipleSelection || isFocusNavigation)) {
        tree.setExpandedKeys(new Set([...keysToExpand, ...tree.expandedKeys]));
        setInteractionModality('keyboard');
      }
    }, [tree, focusedItem, isMultipleSelection, state.selectionManager]);

    // TODO: Sail Checkbox has a visual bug in Chrome when the values of indeterminate and checked are modified in the same render.
    // This is a workaround to avoid it, but this bug must be fixed directly in Checkbox
    const [indeterminate, setIndeterminate] = React.useState(
      hasItemsSelected && !areAllSelected,
    );

    React.useEffect(() => {
      setIndeterminate(hasItemsSelected && !areAllSelected);
    }, [hasItemsSelected, areAllSelected]);

    // TODO: Implement disable for groups

    return (
      <view.div {...props} {...itemProps}>
        {title ? (
          <Header
            id={headingId}
            role="presentation"
            inherits={subviews.header}
            onClick={() => {
              if (tree.expandable) {
                tree.selectionManager.setFocusedKey(null);
                tree.toggleKey(key);
              }
            }}
            onMouseDown={(e) => {
              // Prevent focus going to the FauxTree when clicking the section header.
              if (headerRef.current?.contains(e.target as Node)) {
                e.preventDefault();
              }
            }}
            uses={[assignProps(tree.expandable ? triggerProps : {})]}
            css={{
              width: 'fill',
              alignItems: 'flex-start',
              cursor: tree.expandable ? 'pointer' : 'default',
            }}
            ref={headerRef}
          >
            {!isMultipleSelection ? (
              <>
                {tree.expandable ? (
                  <Icon
                    size="xsmall"
                    icon={chevronDown}
                    css={{
                      transitionProperty: 'transform',
                      transitionDuration: 'medium',
                      transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                      marginX: 'xxsmall',
                    }}
                  />
                ) : null}
                {title}
              </>
            ) : (
              <>
                <view.div
                  css={{
                    stack: 'x',
                    gap: 'space.75',
                    alignItems: 'center',
                  }}
                >
                  <view.div
                    css={{
                      stack: 'x',
                      gap: 'space.75',
                      alignItems: 'center',
                      minHeight: 'space.250',
                    }}
                  >
                    {groupSelectionAllEnabled ? (
                      // Let's use this wrapping div to prevent the click event from propagating to the header
                      // and toggling the group open/close state when clicking on the checkbox.
                      <view.div
                        css={{
                          width: 'fit',
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                      >
                        <Checkbox
                          indeterminate={indeterminate}
                          tabIndex={-1}
                          label={title}
                          subviews={{
                            label: {
                              css: {
                                marginLeft: '-2px', // Customize gap between checkbox and label
                                font: 'heading.xsmall',
                                color: 'form',
                                cursor: !isDisabled ? 'pointer' : 'default',
                              },
                            },
                            input: {
                              css: {
                                ':focus': {
                                  focusRing: 'none',
                                },
                              },
                            },
                          }}
                          checked={areAllSelected}
                          disabled={isDisabled}
                          onChange={() => {
                            if (areAllEnabledSelected) {
                              state.selectionManager.setSelectedKeys(
                                uncheckableKeys,
                              );
                            } else {
                              state.selectionManager.setSelectedKeys(
                                checkableKeys,
                              );
                            }
                          }}
                        />
                      </view.div>
                    ) : (
                      <Label css={{font: 'heading.xsmall', color: 'form'}}>
                        {title}
                      </Label>
                    )}
                  </view.div>
                  {selectedCount > 0 && counterEnabled ? (
                    <Counter data-testid={`region-count-${id}`}>
                      {selectedCount}
                    </Counter>
                  ) : null}
                </view.div>
                {tree.expandable ? (
                  <Icon
                    size="xsmall"
                    icon={chevronDown}
                    css={{
                      transitionProperty: 'transform',
                      transitionDuration: 'medium',
                      transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                      marginX: 'xxsmall',
                      cursor: 'pointer',
                    }}
                  />
                ) : null}
              </>
            )}
          </Header>
        ) : null}

        <section role="region" id={id} {...groupProps}>
          <CollectionGroupContext.Provider value={key}>
            {isOpen ? children : null}
          </CollectionGroupContext.Provider>
        </section>
      </view.div>
    );
  },
  {
    css: {
      padding: 'xsmall',
      bleedX: 'xsmall',
      borderWidth: '0',
      borderTopWidth: 'small',
      borderStyle: 'solid',
      borderColor: 'border',

      ':first-of-type': {
        borderTopWidth: '0',
        bleedTop: 'xsmall',
      },

      ':last-of-type': {
        bleedBottom: 'xsmall',
      },
    },
    subviews: {
      header: {
        css: {
          stack: 'x',
          gap: 'space.75',
          distribute: 'packed',
          font: 'heading.xsmall',
          color: 'form',
          paddingY: 'space.75',
          paddingX: 'space.125',
          minHeight: 'xlarge',
          userSelect: 'none',
        },
      },
      list: {css: {stack: 'y'}},
    },
  },
);
